import { RefObject, useEffect } from 'react';

const MIN_SCALE = 0.65;
const MOBILE_BREAKPOINT = 960;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Scroll-linked shrink for the hero video. As the playground scrolls into view
 * the visual scales from 1 down to MIN_SCALE, retracting its -120px bleed so it
 * no longer protrudes over the white playground section. Anchored at the top
 * (transform-origin lives in CSS) so the bottom pulls up in place.
 *
 * Mirrors the rAF/scroll/ResizeObserver pattern of usePageBgGradient.
 * Desktop-only; honors prefers-reduced-motion by applying the final state.
 */
export const useHeroVideoShrink = (visualRef: RefObject<HTMLElement>): void => {
  useEffect(() => {
    const visual = visualRef.current;
    const hero = document.getElementById('home');
    if (!visual || !hero) return;

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    let raf = 0;
    const update = () => {
      raf = 0;

      // No protrusion below 960px (single-column layout, margin-bottom: 0).
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        visual.style.transform = '';
        return;
      }

      if (reduceMotion) {
        visual.style.transform = `scale(${MIN_SCALE})`;
        return;
      }

      // Progress is how far we've scrolled past the top of the hero, so the
      // video is full-size at the top of the page and shrinks as the hero
      // scrolls away — fully shrunk by ~one hero-height (playground in view).
      const heroRect = hero.getBoundingClientRect();
      const scrolled = -heroRect.top;
      const distance = heroRect.height;
      const progress = clamp(scrolled / distance, 0, 1);
      const scale = 1 - progress * (1 - MIN_SCALE);
      visual.style.transform = `scale(${scale.toFixed(4)})`;
    };

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    const ro = new ResizeObserver(schedule);
    ro.observe(hero);

    document.fonts?.ready.then(schedule).catch(() => undefined);

    update();

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
      visual.style.transform = '';
    };
  }, [visualRef]);
};
